import * as React from 'react';
import * as moment from 'moment';
import {FormComponentProps} from "antd/lib/form";
import DatePickerFormItem from "../common/form-items/DatePickerFormItem";
import locales from "../locales";
import {combineMoments, MOMENT_DATE_FORMAT, toEndOfDay} from "../utils/date-utils";
import TimePickerFormItem from "../common/form-items/TimePickerFormItem";
import {Role} from "../account/AccountProvider";
import {isEqual} from "lodash";
import {DateRange, getDateRangeDiffInMillis} from "../common/dtos";
import {EventDto} from "./dtos";
import {isEventDateDisabled} from "./event-utils";
import {calendarMinuteStep} from "../event/calendar-utils";


const getAdjustedMinTime = (time: moment.Moment): moment.Moment => {
  return time.clone().add(calendarMinuteStep, 'minutes');
}

interface EventTimeRangePickerProps extends FormComponentProps {
  role: Role;
  disabled: boolean;
  fieldIdDate: string;
  fieldIdStart: string;
  fieldIdEnd: string;
  event?: EventDto;
  initialDateRange?: DateRange;
  onStartChanged?: (start: moment.Moment) => void;
}

interface EventTimeRangePickerState {
  initialDateRange?: DateRange;
  minTime?: moment.Moment;
  timeDeltaInMillis: number;
}

class EventTimeRangePicker extends React.Component<EventTimeRangePickerProps, EventTimeRangePickerState> {

  constructor(props: EventTimeRangePickerProps) {
    super(props);
    if (props.initialDateRange) {
      this.state = {
        initialDateRange: props.initialDateRange,
        minTime: getAdjustedMinTime(props.initialDateRange.start),
        timeDeltaInMillis: getDateRangeDiffInMillis(props.initialDateRange),
      };
    } else {
      this.state = {
        timeDeltaInMillis: calendarMinuteStep * 60000,
      };
    }
  }

  public componentDidUpdate(prevProps: Readonly<EventTimeRangePickerProps>, prevState: Readonly<EventTimeRangePickerState>, snapshot?: any) {
    const {event} = this.props;
    if (event && !isEqual(prevProps.event, event)) {
      this.setState({
        minTime: getAdjustedMinTime(event.start),
        timeDeltaInMillis: event.end.diff(event.start),
      });
    }
  }

  public render(): React.ReactNode {
    const {form, disabled, fieldIdDate, fieldIdStart, fieldIdEnd, event} = this.props;
    const {minTime, initialDateRange} = this.state;

    let initialDate: moment.Moment | undefined
    let initialStart: moment.Moment | undefined
    let initialEnd: moment.Moment | undefined
    if (event) {
      initialDate = event.start;
      initialStart = event.start;
      initialEnd = event.end;
    } else if (initialDateRange) {
      initialDate = initialDateRange.start;
      initialStart = initialDateRange.start;
      initialEnd = initialDateRange.end;
    }


    return (
      <>
        <DatePickerFormItem
          form={form}
          fieldId={fieldIdDate}
          label={locales.event_date}
          initialValue={initialDate}
          format={MOMENT_DATE_FORMAT}
          disabled={disabled}
          required={true}
          showTime={false}
          showClear={false}
          disabledDate={this.isDateDisabled}
        />
        <TimePickerFormItem
          form={form}
          fieldId={fieldIdStart}
          label={locales.event_time_range_start}
          initialValue={initialStart}
          minuteStep={calendarMinuteStep}
          disabled={disabled}
          required={true}
          onChange={this.onStartTimeChange}
          rules={[
            {validator: this.validateStart}
          ]}
        />
        <TimePickerFormItem
          form={form}
          fieldId={fieldIdEnd}
          label={locales.event_time_range_end}
          initialValue={initialEnd}
          minuteStep={calendarMinuteStep}
          minTime={minTime}
          disabled={disabled}
          required={true}
          onChange={this.onEndTimeChange}
          rules={[
            {validator: this.validateEnd}
          ]}
        />
      </>
    );
  }

  private validateStart = (rule: any, start: moment.Moment | undefined, callback: (msg?: string) => void) => {
    const {form, fieldIdEnd} = this.props;
    const end = form.getFieldValue(fieldIdEnd) as (undefined | moment.Moment);
    if (start && end && start.isAfter(end)) {
      callback(locales.event_time_range_error);
      return;
    }
    callback();
  }

  private validateEnd = (rule: any, end: moment.Moment | undefined, callback: (msg?: string) => void) => {
    const {form, fieldIdStart} = this.props;
    const start = form.getFieldValue(fieldIdStart) as (undefined | moment.Moment);
    if (start && end && end.isBefore(start)) {
      callback(locales.event_time_range_error);
      return;
    }
    callback();
  }

  private isDateDisabled = (date: moment.Moment): boolean => {
    return isEventDateDisabled(this.props.role, date)
  }

  private onStartTimeChange = (startTime: moment.Moment) => {
    const {form, fieldIdDate, fieldIdEnd} = this.props;
    const {timeDeltaInMillis} = this.state;

    const date = form.getFieldValue(fieldIdDate) as (undefined | moment.Moment);
    const adjustedEnd = startTime.clone().add(timeDeltaInMillis, 'millisecond');

    if (date) {
      const maxEnd = combineMoments(toEndOfDay(date), moment("23:30", "hh:mm"))
      const end = moment.min(adjustedEnd, maxEnd);
      form.setFieldsValue({[fieldIdEnd]: end})
      this.props.onStartChanged?.(combineMoments(date, startTime));
    } else {
      form.setFieldsValue({[fieldIdEnd]: adjustedEnd})
    }

    this.setState({
      minTime: getAdjustedMinTime(startTime),
    });
  }

  private onEndTimeChange = (endTime: moment.Moment) => {
    const {form, fieldIdStart} = this.props;
    const start = form.getFieldValue(fieldIdStart) as (undefined | moment.Moment);

    if (start) {
      this.setState({
        timeDeltaInMillis: endTime.diff(start),
      });
    }
  }
}

export default EventTimeRangePicker;


// WEBPACK FOOTER //
// ./src/event-details/EventTimeRangePicker.tsx